// Full-prefix verification for "Reselect files to resume" (§6). Hashes the reselected file in the same
// 8 MB segments up to the committed offset (one segment in memory at a time, Web Crypto) and compares
// every segment, plus the tail beyond the last boundary. Any mismatch → not the same file.
import { get } from '../lib/api';

type ChunkHashes = { offset: number; chunkSize: number; hashes: string[]; tailHash: string | null };

const hex = (buf: ArrayBuffer) => Array.from(new Uint8Array(buf), (b) => b.toString(16).padStart(2, '0')).join('');

export async function sha256Hex(blob: Blob): Promise<string> {
  return hex(await crypto.subtle.digest('SHA-256', await blob.arrayBuffer()));
}

export async function verifyPrefix(reservationId: string, file: File, onProgress?: (p: number) => void): Promise<boolean> {
  const info = await get<ChunkHashes>(`/api/upload-reservations/${reservationId}/chunk-hashes`);
  return verifyAgainst(info, file, onProgress);
}

export async function verifyAgainst(info: ChunkHashes, file: Blob, onProgress?: (p: number) => void): Promise<boolean> {
  if (file.size < info.offset) return false;
  const cs = info.chunkSize;
  const full = Math.floor(info.offset / cs);
  if (info.hashes.length < full) return false;
  const steps = full + (info.offset % cs ? 1 : 0) || 1;
  for (let i = 0; i < full; i++) {
    const h = await sha256Hex(file.slice(i * cs, (i + 1) * cs));
    if (h !== info.hashes[i]!.toLowerCase()) return false;
    onProgress?.((i + 1) / steps);
  }
  const tailStart = full * cs;
  if (info.offset > tailStart) {
    if (!info.tailHash) return false;
    const h = await sha256Hex(file.slice(tailStart, info.offset));
    if (h !== info.tailHash.toLowerCase()) return false;
  }
  onProgress?.(1);
  return true;
}
